import { api } from "aldoo-ra/API";

// Create API client for product-licensing service
const licensingAPI = api("product-licensing");

/**
 * PUBLIC ACTIONS - No authentication required
 */

/**
 * Activate a license code
 * @param {Object} params - Activation parameters
 * @param {string} params.code - License code to activate
 * @param {string} [params.deviceId] - Device identifier (optional - uses current user's device if not provided)
 * @returns {Promise<Object>} - API response with activation result
 */
export const ActivateLicense = async ({ code, deviceId }) => {
    try {
        const payload = { code };
        if (deviceId) {
            payload.deviceId = deviceId;
        }
        
        const response = await licensingAPI({
            activate_license: payload
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Check license validity
 * @param {Object} params - Check parameters
 * @param {string} params.code - License code
 * @param {string} [params.deviceId] - Device identifier (optional - uses current user's device if not provided)
 * @returns {Promise<Object>} - API response with license status
 */
export const CheckLicense = async ({ code, deviceId }) => {
    try {
        const payload = { code };
        if (deviceId) {
            payload.deviceId = deviceId;
        }
        
        const response = await licensingAPI({
            check_license: payload
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * LICENSE PACK ACTIONS
 */

/**
 * Create a new license pack
 * @param {Object} packData - Pack data
 * @param {string} packData.name - Pack name
 * @param {string} [packData.description] - Pack description
 * @param {string} [packData.state] - Pack state (active/paused)
 * @returns {Promise<Object>} - API response with created pack
 */
export const CreatePack = async (packData) => {
    try {
        const response = await licensingAPI({
            create_pack: packData
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Update an existing license pack
 * @param {Object} updateData - Update data
 * @param {string} updateData.packId - Pack ID to update
 * @param {string} [updateData.name] - New name
 * @param {string} [updateData.description] - New description
 * @param {string} [updateData.state] - New state
 * @returns {Promise<Object>} - API response with updated pack
 */
export const UpdatePack = async (updateData) => {
    try {
        const response = await licensingAPI({
            update_pack: updateData
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Delete a license pack
 * @param {string} packId - Pack ID to delete
 * @returns {Promise<Object>} - API response
 */
export const DeletePack = async (packId) => {
    try {
        const response = await licensingAPI({
            delete_pack: { packId }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Get list of license packs (with pagination)
 * @param {Object} options - Query options
 * @param {number} [options.page] - Page number
 * @param {number} [options.itemsPerPage] - Items per page
 * @param {Object} [options.search] - Search criteria
 * @param {Object} [options.match] - Match criteria
 * @param {Object} [options.sort] - Sort criteria
 * @returns {Promise<Object>} - API response with packs list
 */
export const GetPacks = async (options = {}) => {
    try {
        const response = await licensingAPI({
            get_packs: {
                page: options.page || 1,
                itemsPerPage: options.itemsPerPage || 10,
                search: options.search,
                match: options.match,
                sort: options.sort || { created: -1 }
            }
        });


        if (response.result) {
            return response;
        } else {
            return {
                result: false,
                error: response.error || 'Failed to fetch packs',
                data: [],
                totalItems: 0
            };
        }
    } catch (error) {
        throw error;
    }
};

/**
 * Get detailed information about a specific pack
 * @param {string} packId - Pack ID
 * @returns {Promise<Object>} - API response with pack details
 */
export const GetPackDetails = async (packId) => {
    try {

        const response = await licensingAPI({
            get_pack_details: { packId }
        });


        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * LICENSE MODEL ACTIONS
 */

/**
 * Create a new license model
 * @param {Object} modelData - Model data
 * @param {string} modelData.name - Model name
 * @param {string} modelData.codePattern - Code pattern (e.g., "XXXX-XXXX")
 * @param {string} modelData.codeSet - Character set (e.g., "A-Z,0-9")
 * @param {string} [modelData.explicitCode] - Explicit code for 3rd party
 * @param {number} modelData.seats - Number of seats
 * @param {string} modelData.productId - Product identifier
 * @param {number} modelData.validity - Validity in days (0 for lifetime)
 * @returns {Promise<Object>} - API response with created model
 */
export const CreateModel = async (modelData) => {
    try {
        const response = await licensingAPI({
            create_model: modelData
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Update an existing license model
 * @param {Object} updateData - Update data (includes modelId and fields to update)
 * @returns {Promise<Object>} - API response with updated model
 */
export const UpdateModel = async (updateData) => {
    try {
        const response = await licensingAPI({
            update_model: updateData
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Delete a license model
 * @param {string} modelId - Model ID to delete
 * @returns {Promise<Object>} - API response
 */
export const DeleteModel = async (modelId) => {
    try {
        const response = await licensingAPI({
            delete_model: { modelId }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Get list of license models (with pagination)
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - API response with models list
 */
export const GetModels = async (options = {}) => {
    try {
        const response = await licensingAPI({
            get_models: {
                page: options.page || 1,
                itemsPerPage: options.itemsPerPage || 10,
                search: options.search,
                match: options.match,
                sort: options.sort || { created: -1 }
            }
        });


        if (response.result) {
            return response;
        } else {
            return {
                result: false,
                error: response.error || 'Failed to fetch models',
                data: [],
                totalItems: 0
            };
        }
    } catch (error) {
        throw error;
    }
};

/**
 * Get detailed information about a specific model
 * @param {string} modelId - Model ID
 * @returns {Promise<Object>} - API response with model details
 */
export const GetModelDetails = async (modelId) => {
    try {
        const response = await licensingAPI({
            get_model_details: { modelId }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * LICENSE ACTIONS
 */

/**
 * Generate bulk licenses
 * @param {Object} generateData - Generation data
 * @param {string} generateData.packId - Pack ID
 * @param {string} [generateData.modelId] - Model ID to use as template
 * @param {number} [generateData.count] - Number of licenses to generate
 * @param {number} [generateData.seats] - Seats per license
 * @param {string} [generateData.productId] - Product ID
 * @param {number} [generateData.validity] - Validity in days
 * @param {Array<string>} [generateData.supported_platforms] - Supported platforms
 * @param {Array<string>} [generateData.explicitCodes] - Explicit codes to use
 * @returns {Promise<Object>} - API response with generated licenses
 */
export const GenerateLicenses = async (generateData) => {
    try {

        const response = await licensingAPI({
            generate_licenses: generateData
        });


        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Get list of licenses
 * @param {Object} options - Query options
 * @param {string} [options.packId] - Filter by pack ID
 * @returns {Promise<Object>} - API response with licenses list
 */
export const GetLicenses = async (options = {}) => {
    try {
        const response = await licensingAPI({
            get_licenses: {
                page: options.page || 1,
                itemsPerPage: options.itemsPerPage || 10,
                search: options.search,
                match: options.match,
                sort: options.sort || { created: -1 },
                packId: options.packId
            }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Get detailed information about a specific license
 * @param {string} licenseId - License ID
 * @returns {Promise<Object>} - API response with license details
 */
export const GetLicenseDetails = async (licenseId) => {
    try {
        const response = await licensingAPI({
            get_license_details: { licenseId }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Get users for a specific license with pagination
 * @param {Object} options - Query options
 * @param {string} options.licenseId - License ID
 * @param {number} [options.page] - Page number
 * @param {number} [options.itemsPerPage] - Items per page
 * @param {Object} [options.search] - Search criteria
 * @param {Object} [options.sort] - Sort criteria
 * @returns {Promise<Object>} - API response with license users
 */
export const GetLicenseUsers = async (options = {}) => {
    try {
        // Transform search parameters to be backend-friendly (same as devices)
        let searchParams = options.search;
        
        // If search contains $or (multi-field search), convert to case-insensitive regex
        if (searchParams && searchParams.$or && Array.isArray(searchParams.$or)) {
            const searchTerm = searchParams.$or[0] ? Object.values(searchParams.$or[0])[0] : '';
            if (searchTerm) {
                // Try case-insensitive regex approach for name and email
                searchParams = {};
                searchParams.$or = [
                    { name: { $regex: searchTerm, $options: 'i' } },
                    { email: { $regex: searchTerm, $options: 'i' } }
                ];
            }
        }
        
        const response = await licensingAPI({
            get_license_users: {
                licenseId: options.licenseId,
                page: options.page || 1,
                itemsPerPage: options.itemsPerPage || 10,
                search: searchParams,
                sort: options.sort || { 'name': 1 }
            }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Get devices for a specific license with pagination
 * @param {Object} options - Query options
 * @param {string} options.licenseId - License ID
 * @param {number} [options.page] - Page number
 * @param {number} [options.itemsPerPage] - Items per page
 * @param {Object} [options.search] - Search criteria
 * @param {Object} [options.sort] - Sort criteria
 * @returns {Promise<Object>} - API response with license devices
 */
export const GetLicenseDevices = async (options = {}) => {
    try {
        // Transform search parameters to be backend-friendly
        let searchParams = options.search;
        
        // If search contains $or (multi-field search), convert to case-insensitive regex
        if (searchParams && searchParams.$or && Array.isArray(searchParams.$or)) {
            const searchTerm = searchParams.$or[0] ? Object.values(searchParams.$or[0])[0] : '';
            if (searchTerm) {
                // Try case-insensitive regex approach
                searchParams = {};
                searchParams.$or = [
                    { name: { $regex: searchTerm, $options: 'i' } },
                    { model: { $regex: searchTerm, $options: 'i' } },
                    { platform: { $regex: searchTerm, $options: 'i' } }
                ];
            }
        }
        
        const payload = {
            get_license_devices: {
                licenseId: options.licenseId,
                page: options.page || 1,
                itemsPerPage: options.itemsPerPage || 10,
                search: searchParams,
                sort: options.sort || { 'created': -1 }
            }
        };
        
        const response = await licensingAPI(payload);
        
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Update license information
 * @param {Object} updateData - Update data
 * @param {string} updateData.licenseId - License ID
 * @param {number} [updateData.seats] - New seats count
 * @param {number} [updateData.validity] - New validity
 * @param {string} [updateData.status] - New status
 * @param {string} [updateData.productId] - New product ID
 * @param {string} [updateData.ownerId] - New owner user ID (for transferring ownership)
 * @returns {Promise<Object>} - API response with updated license
 */
export const UpdateLicense = async (updateData) => {
    try {
        const response = await licensingAPI({
            update_license: updateData
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Delete multiple licenses
 * @param {Array<string>} licenseIds - Array of license IDs to delete
 * @returns {Promise<Object>} - API response
 */
export const DeleteLicenses = async (licenseIds) => {
    try {
        const response = await licensingAPI({
            delete_licenses: { licenseIds }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Export licenses to a text file
 * @param {Object} options - Export options
 * @param {Object} [options.search] - Search criteria
 * @param {Object} [options.match] - Match criteria
 * @param {Object} [options.sort] - Sort criteria
 * @param {string} [options.packId] - Filter by pack ID
 * @param {string} [options.saveAs] - Filename prefix for the export
 * @returns {Promise<Object>} - API response with download link and metadata
 */
export const ExportLicenses = async (options = {}) => {
    try {

        const response = await licensingAPI({
            export_licenses: {
                search: options.search,
                match: options.match,
                sort: options.sort || { created: -1 },
                packId: options.packId,
                saveAs: options.saveAs || 'licenses'
            }
        });


        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Remove a device from a license
 * @param {Object} params - Parameters
 * @param {string} params.licenseId - License ID
 * @param {string} params.deviceId - Device ID to remove
 * @returns {Promise<Object>} - API response
 */
export const DeactivateDevice = async ({ licenseId, deviceId }) => {
    try {
        const response = await licensingAPI({
            deactivate_device: { licenseId, deviceId }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Remove multiple devices from a license (bulk operation)
 * @param {Object} params - Parameters
 * @param {string} params.licenseId - License ID
 * @param {Array<string>} params.deviceIds - Array of device IDs to deactivate
 * @returns {Promise<Object>} - API response with bulk operation results
 */
export const DeactivateDevices = async ({ licenseId, deviceIds }) => {
    try {
        const response = await licensingAPI({
            deactivate_devices: { licenseId, deviceIds }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * USER MANAGEMENT ACTIONS
 */

/**
 * Add a user to a license
 * @param {Object} params - Parameters
 * @param {string} params.licenseId - License ID
 * @param {string} params.userId - User ID to add
 * @param {boolean} [params.makePrimary] - Whether to make this user the primary owner (default: false)
 * @returns {Promise<Object>} - API response
 */
export const AddUserToLicense = async ({ licenseId, userId, makePrimary = false }) => {
    try {
        const response = await licensingAPI({
            add_user_to_license: { licenseId, userId, makePrimary }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Remove a user from a license
 * @param {Object} params - Parameters
 * @param {string} params.licenseId - License ID
 * @param {string} params.userId - User ID to remove
 * @returns {Promise<Object>} - API response
 */
export const RemoveUserFromLicense = async ({ licenseId, userId }) => {
    try {
        const response = await licensingAPI({
            remove_user_from_license: { licenseId, userId }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Remove multiple users from a license (bulk operation)
 * @param {Object} params - Parameters
 * @param {string} params.licenseId - License ID
 * @param {Array<string>} params.userIds - Array of user IDs to remove
 * @returns {Promise<Object>} - API response with bulk operation results
 */
export const RemoveUsersFromLicense = async ({ licenseId, userIds }) => {
    try {
        const response = await licensingAPI({
            remove_users_from_license: { licenseId, userIds }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Search for users to add to licenses
 * @param {Object} options - Search options
 * @param {string} [options.search] - Search term for name/email
 * @param {number} [options.limit] - Maximum results to return
 * @returns {Promise<Object>} - API response with users list
 */
export const SearchUsers = async (options = {}) => {
    try {
        // This will need to be implemented in the backend
        // For now, using a placeholder that calls a generic user search
        const response = await licensingAPI({
            search_users: {
                search: options.search || "",
                limit: options.limit || 10
            }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Attach a license to an existing user
 * @param {Object} params - Parameters
 * @param {string} params.code - License code
 * @param {string} params.userId - User ID
 * @returns {Promise<Object>} - API response
 */
export const AttachLicense = async ({ code, userId }) => {
    try {
        const response = await licensingAPI({
            attach_license: { code, userId }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * UTILITY ACTIONS
 */

/**
 * Get licensing system statistics
 * @returns {Promise<Object>} - API response with statistics
 */
export const GetStatistics = async () => {
    try {
        const response = await licensingAPI({
            get_statistics: {}
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Validate a license code
 * @param {string} code - License code to validate
 * @returns {Promise<Object>} - API response with validation result
 */
export const ValidateCode = async (code) => {
    try {
        const response = await licensingAPI({
            validate_code: { code }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Get store products for licensing
 * @param {Object} options - Query options
 * @param {number} [options.page] - Page number
 * @param {number} [options.itemsPerPage] - Items per page (-1 for all)
 * @param {Object} [options.search] - Search criteria
 * @param {Object} [options.match] - Match criteria
 * @param {Object} [options.sort] - Sort criteria
 * @returns {Promise<Object>} - API response with store products
 */
export const GetStoreProducts = async (options = {}) => {
    try {
        const response = await licensingAPI({
            get_store_products: {
                page: options.page || 1,
                itemsPerPage: options.itemsPerPage || -1, // Get all by default
                search: options.search,
                match: options.match,
                sort: options.sort || { created: -1 }
            }
        });

        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * ENHANCED HELPER FUNCTIONS
 */

/**
 * Get all packs (without pagination) - Enhanced version
 * @returns {Promise<Array>} - Array of all active packs
 */
export const GetAllPacks = async () => {
    try {

        const response = await GetPacks({ itemsPerPage: -1 });


        if (response.result) {

            // Log first pack details if available
            if (response.data && response.data.length > 0) {
                // Pack details available
            }

            // Filter active packs
            const activePacks = response.data.filter(pack => pack.state === 'active');

            return activePacks;
        } else {
            return [];
        }
    } catch (error) {
        throw error;
    }
};


/**
 * Get all models (without pagination) - Enhanced version
 * @returns {Promise<Array>} - Array of all models
 */
export const GetAllModels = async () => {
    try {

        const response = await GetModels({ itemsPerPage: -1 });


        if (response.result) {

            // Log first model details if available
            if (response.data && response.data.length > 0) {
                // Model details available
            }

            return response.data;
        } else {
            return [];
        }
    } catch (error) {
        throw error;
    }
};

/**
 * Get licenses by status
 * @param {string} status - License status to filter by
 * @param {Object} options - Additional query options
 * @returns {Promise<Object>} - API response with filtered licenses
 */
export const GetLicensesByStatus = async (status, options = {}) => {
    try {
        const response = await GetLicenses({
            ...options,
            match: {
                ...(options.match || {}),
                status
            }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Get licenses for the current authenticated user
 * @returns {Promise<Object>} - API response with user's licenses
 */
export const GetUserLicenses = async () => {
    try {
        const response = await licensingAPI({
            get_user_licenses: {}
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Get devices for the current authenticated user across all their licenses
 * @param {Object} options - Query options
 * @param {number} [options.page] - Page number
 * @param {number} [options.itemsPerPage] - Items per page
 * @param {Object} [options.search] - Search criteria
 * @param {Object} [options.sort] - Sort criteria
 * @returns {Promise<Object>} - API response with user's devices
 */
export const GetUserDevices = async (options = {}) => {
    try {
        const response = await licensingAPI({
            get_user_devices: {
                page: options.page || 1,
                itemsPerPage: options.itemsPerPage || 10,
                search: options.search,
                match: options.match,
                sort: options.sort || { 'created': -1 }
            }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * DEBUG AND TEST FUNCTIONS
 */

/**
 * Test API connectivity
 * @returns {Promise<boolean>} - True if API is working
 */
export const TestAPIConnection = async () => {
    try {

        // Test basic API call
        const testResponse = await GetStatistics();


        if (testResponse.result !== false) {
            return true;
        } else {
            return false;
        }
    } catch (error) {
        return false;
    }
};

/**
 * Test models API specifically
 * @returns {Promise<Object>} - Test results
 */
export const TestModelsAPI = async () => {
    try {

        // Test both functions
        const [allModels, paginatedModels] = await Promise.all([
            GetAllModels(),
            GetModels({ page: 1, itemsPerPage: 5 })
        ]);


        return {
            allModels: {
                success: Array.isArray(allModels),
                count: allModels?.length || 0,
                sample: allModels?.[0] || null
            },
            paginatedModels: {
                success: paginatedModels?.result === true,
                count: paginatedModels?.data?.length || 0,
                totalItems: paginatedModels?.totalItems || 0
            }
        };
    } catch (error) {
        return { error: error.message };
    }
};

/**
 * Test packs API specifically
 * @returns {Promise<Object>} - Test results
 */
export const TestPacksAPI = async () => {
    try {

        // Test both functions
        const [allPacks, paginatedPacks] = await Promise.all([
            GetAllPacks(),
            GetPacks({ page: 1, itemsPerPage: 5 })
        ]);


        return {
            allPacks: {
                success: Array.isArray(allPacks),
                count: allPacks?.length || 0,
                sample: allPacks?.[0] || null
            },
            paginatedPacks: {
                success: paginatedPacks?.result === true,
                count: paginatedPacks?.data?.length || 0,
                totalItems: paginatedPacks?.totalItems || 0
            }
        };
    } catch (error) {
        return { error: error.message };
    }
};

// Export all functions as default object
export default {
    // Public actions
    ActivateLicense,
    CheckLicense,

    // Pack management
    CreatePack,
    UpdatePack,
    DeletePack,
    GetPacks,
    GetPackDetails,
    GetAllPacks,

    // Model management
    CreateModel,
    UpdateModel,
    DeleteModel,
    GetModels,
    GetModelDetails,
    GetAllModels,

    // License management
    GenerateLicenses,
    GetLicenses,
    GetLicenseDetails,
    UpdateLicense,
    DeleteLicenses,
    ExportLicenses,
    DeactivateDevice,
    GetLicensesByStatus,
    GetUserLicenses,
    GetUserDevices,

    // User management
    AddUserToLicense,
    RemoveUserFromLicense,
    RemoveUsersFromLicense,
    SearchUsers,
    AttachLicense,
    GetLicenseUsers,
    GetLicenseDevices,
    DeactivateDevices,

    // Utilities
    GetStatistics,
    ValidateCode,

    // Debug functions
    TestAPIConnection,
    TestModelsAPI,
    TestPacksAPI
};